/**
 * LineChart
 *
 * Inline-SVG line chart. No chart library per CONSTITUTION.
 */

interface LineChartPoint {
  label: string;
  value: number;
}

interface LineChartProps {
  points: LineChartPoint[];
  width?: number;
  height?: number;
  color?: string;
  valueFormatter?: (v: number) => string;
}

export function LineChart({
  points,
  width = 400,
  height = 120,
  color = '#7c3aed',
  valueFormatter = (v) => `$${v.toFixed(2)}`,
}: LineChartProps) {
  if (points.length === 0) {
    return (
      <svg width={width} height={height} aria-label="Line chart — no data">
        <text x={width / 2} y={height / 2} textAnchor="middle" fill="#71717a" fontSize="11">
          No data
        </text>
      </svg>
    );
  }

  const pad = { top: 8, right: 8, bottom: 24, left: 8 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const values = points.map((p) => p.value);
  const maxVal = Math.max(...values, 0.01);

  const polyPoints = points
    .map((p, i) => {
      const x = pad.left + (i / Math.max(points.length - 1, 1)) * innerW;
      const y = pad.top + (1 - p.value / maxVal) * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  // X-axis labels — show first, middle, last
  const labelIndices = [0, Math.floor(points.length / 2), points.length - 1].filter(
    (v, i, arr) => arr.indexOf(v) === i
  );

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={points.map((p) => `${p.label}: ${valueFormatter(p.value)}`).join(', ')}
    >
      {/* Baseline */}
      <line
        x1={pad.left}
        y1={pad.top + innerH}
        x2={pad.left + innerW}
        y2={pad.top + innerH}
        stroke="#3f3f46"
        strokeWidth="1"
      />

      {/* Line */}
      <polyline
        points={polyPoints}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* X-axis labels */}
      {labelIndices.map((idx) => {
        const p = points[idx];
        const x = pad.left + (idx / Math.max(points.length - 1, 1)) * innerW;
        return (
          <text
            key={idx}
            x={x}
            y={height - 4}
            textAnchor="middle"
            fill="#71717a"
            fontSize="9"
          >
            {p.label}
          </text>
        );
      })}
    </svg>
  );
}
